import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowRight, Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { request, revokeSession } from "../lib/api";
import type { PortalRole } from "../lib/auth";
import type { LoginResponse } from "../types";
import {
  errorMessage,
  inputClass,
  labelClass,
  LanguageSwitcher,
  primaryButtonClass,
} from "../components/ui";

export function Login({
  onLogin,
}: {
  onLogin: (value: { token: string; assignedID: string; role: PortalRole }) => void;
}) {
  const { t } = useTranslation();
  const [assignedID, setAssignedID] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const result = await request<LoginResponse>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({
          assigned_id: assignedID,
          password,
          phone_time: new Date().toISOString(),
          connection: navigator.onLine ? "online" : "offline",
          device: { user_agent: navigator.userAgent },
        }),
      });
      if (result.user.role !== "admin" && result.user.role !== "super_admin") {
        void revokeSession(result.token).catch(() => undefined);
        throw new Error("Admin access required.");
      }
      onLogin({ token: result.token, assignedID: result.user.assigned_id, role: result.user.role });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-surface-app px-4 py-10">
      <div className="absolute inset-x-0 top-0 h-1 bg-primary" />
      <form
        className="grid w-full max-w-md gap-6 rounded-xl border border-border bg-surface p-7 shadow-lg sm:p-9"
        onSubmit={submit}
      >
        <div className="grid gap-5 text-center">
          <div>
            <div className="text-xl font-bold tracking-tight text-content-primary">
              <span className="text-primary">CERP</span>{" "}
              <span className="font-medium">PD Days</span>
            </div>
            <p className="mt-1 text-xs font-medium uppercase tracking-[0.16em] text-content-disabled">
              Research administration
            </p>
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-content-primary">
              {t("adminPortal")}
            </h1>
            <p className="mt-1 text-sm leading-6 text-content-secondary">
              Sign in to manage learning programmes and research data.
            </p>
          </div>
        </div>
        <label className={labelClass}>
          {t("assignedId")}
          <input
            className={inputClass}
            value={assignedID}
            onChange={(event) => setAssignedID(event.target.value)}
            required
          />
        </label>
        <label className={labelClass}>
          {t("passwordOrPin")}
          <span className="relative">
            <input
              className={`${inputClass} pr-11`}
              type={passwordVisible ? "text" : "password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            <button
              className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-content-secondary transition-colors hover:text-content-primary focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary"
              type="button"
              onClick={() => setPasswordVisible((visible) => !visible)}
              aria-label={passwordVisible ? "Hide password" : "Show password"}
              aria-pressed={passwordVisible}
            >
              {passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </span>
        </label>
        {error && (
          <p className="flex items-center gap-1.5 rounded-md bg-danger-bg px-3 py-2 text-sm text-danger">
            <AlertTriangle size={16} className="shrink-0" />
            {error}
          </p>
        )}
        <button className={`${primaryButtonClass} w-full py-3`} disabled={busy}>
          {busy && <Loader2 size={16} className="animate-spin" />}
          {t("signIn")}
          {!busy && <ArrowRight size={16} />}
        </button>
        <div className="flex items-center justify-between border-t border-border pt-5">
          <span className="flex items-center gap-2 text-xs text-content-secondary">
            <ShieldCheck size={15} /> Secure admin access
          </span>
          <LanguageSwitcher />
        </div>
      </form>
    </div>
  );
}
